<template>
  <div class="category-section" :class="{ disabled: disabled }">
    <div class="category-header">
      <div class="category-title">{{ title }}</div>
      <GamSwitch v-bind="toggleSwitch" v-model:checked="isEnabled" />
    </div>

    <div v-if="isEnabled && !simple && Object.keys(subTypeLabels).length > 0" class="sub-types">
      <div v-for="(label, subType) in subTypeLabels" :key="subType" class="sub-type-row">
        <GamSwitch v-bind="subTypeSwitch(subType as string, label)" v-model:checked="subTypeChecked[subType]" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CategoryPreference, NotificationCategory } from '@/core/data/user/user.interface';
import GamSwitch from '@/views/components/GamSwitch.vue';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';
import type { GamSwitchType } from '@/views/composables/models/components/GamSwitch';
import { computed, reactive, watch } from 'vue';

interface Props {
  title: string;
  category: NotificationCategory;
  preference: CategoryPreference;
  subTypeLabels: Record<string, string>;
  simple?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  simple: false,
  disabled: false,
});

const emit = defineEmits<{
  update: [category: NotificationCategory, preference: CategoryPreference];
}>();

const isEnabled = computed({
  get: () => props.preference.enabled,
  set: (val: boolean) => {
    if (val) {
      const allSubTypes = Object.keys(props.subTypeLabels);
      emit('update', props.category, {
        enabled: true,
        mode: props.simple || allSubTypes.length === 0 ? 'all' : 'only',
        subTypes: props.simple || allSubTypes.length === 0 ? [] : allSubTypes,
      });
      allSubTypes.forEach((st) => {
        subTypeChecked[st] = true;
      });
    } else {
      emit('update', props.category, {
        enabled: false,
        mode: 'all',
        subTypes: [],
      });
    }
  },
});

const subTypeChecked = reactive<Record<string, boolean>>({});

const initSubTypes = () => {
  Object.keys(props.subTypeLabels).forEach((subType) => {
    if (props.preference.mode === 'all') {
      subTypeChecked[subType] = true;
    } else {
      subTypeChecked[subType] = props.preference.subTypes.includes(subType);
    }
  });
};

initSubTypes();

const selectedSubTypes = computed(() => {
  return Object.keys(subTypeChecked).filter((key) => subTypeChecked[key]);
});

watch(
  () => props.preference,
  () => {
    initSubTypes();
  },
  { deep: true },
);

watch(
  subTypeChecked,
  () => {
    if (props.preference.enabled && !props.simple) {
      emit('update', props.category, {
        enabled: true,
        mode: 'only',
        subTypes: selectedSubTypes.value,
      });
    }
  },
  { deep: true },
);

const toggleSwitch: GamSwitchType = {
  label: '',
  name: `${props.category}-toggle`,
  type: GamSwitchValueType.SWITCH,
  position: GamSwitchPosition.RIGHT,
  disabled: props.disabled,
};

const subTypeSwitch = (subType: string, label: string): GamSwitchType => ({
  label,
  name: `${props.category}-${subType}`,
  type: GamSwitchValueType.CHECKBOX,
  position: GamSwitchPosition.LEFT,
  disabled: props.disabled,
});
</script>

<style scoped lang="scss">
.category-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-l);
  gap: var(--spacing-md);
  border-radius: var(--radius-normal);
  border: 1px solid var(--color-white-10);

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .category-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-md);

    .category-title {
      font-size: 16px;
      font-weight: 700;
      flex: 1;
      white-space: nowrap;
    }
  }

  .sub-types {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    padding-left: var(--spacing-md);
    margin-top: var(--spacing-l);

    .sub-type-row {
      width: 100%;

      :deep(.gam-switch-container.checkbox) {
        input:checked ~ .checkmark {
          &:before {
            opacity: 0 !important;
          }

          .checkmark-icon svg path,
          .checkmark-icon svg {
            fill: var(--color-pink) !important;
          }
        }
      }
    }
  }
}
</style>
